import { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { T, useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import { Modal, Checkbox, Tooltip, Spin } from 'antd';
import StatusButton from 'components/StatusButton';
import { useAuthorization } from 'components/Authorization';
import { OnOrder } from 'services/api/pay';
import chatttSDK from 'services/chattt';
import { find } from 'lodash-es';

import CurrencyCheckbox from './CurrencyCheckbox';
import PayTypeCheckbox from './PayTypeCheckbox';
import PackageDetail from './PackageDetail';
import StripeSubscriptionModal from './StripeSubscriptionModal';
import ZhixiaGift from './ZhixiaGift';
import usePayPackage from '../hooks/usePayPackage';
import { EnterpriseTotal } from './EnterpriseTotal';
import { NonEnterpriseTotal, NonEnterpriseTotalProps } from './NonEnterpriseTotal';
import { isQuickChat } from 'productLine';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { CompanyAliHelp, Help } from './OrderAI/Help';
import TransComponent from 'components/TransComponent';
import { currencySymbol } from './PriceText';
import { usePricingCatalog } from '../hooks/usePricing';
import {
  catalogPackageCurrencies,
  catalogPackageLabel,
  catalogToPackageInfoV2,
} from '../catalogAdapter';
import { usePriceZones } from '../hooks/usePricing';
import { narrowPriceZone, PriceZoneCode } from '../priceZone';
import ZoneBar from './ZoneBar';
import ZoneChangeDialog from './ZoneChangeDialog';
import { CatalogErrorRetry } from './CatalogErrorRetry';
import { useAutoRenewPayType } from 'hooks/business/pay/usePayTypeState';

export const canPayAirwallex = () => {
  if (!chatttSDK) {
    return true;
  }

  const {
    app: { isSupport },
  } = chatttSDK;

  return isSupport && isSupport.openWindowWhitelist;
};

export interface PackageProps {
  /** 当前价区（页面级状态） */
  zone: PriceZoneCode;
  /** 可购价区集合 */
  zones?: PriceZoneCode[];
  onZoneChange?: (zone: PriceZoneCode) => void;
  onOrder: OnOrder;
  onSubscribed?: () => void;
  onSelectedPackageChange?: (zone: PriceZoneCode, pkg: Pay.PackageV2 | undefined) => void;
}

function Package(props: PackageProps) {
  const { zone, zones = [], onZoneChange, onOrder, onSubscribed, onSelectedPackageChange } = props;

  const { t } = useTranslate();

  const {
    catalog,
    isLoading: catalogLoading,
    isError: catalogError,
    refetch: refetchCatalog,
  } = usePricingCatalog(zone, 'ORDER');
  const { defaultCurrency } = usePriceZones();
  const packageInfo = useMemo(() => catalogToPackageInfoV2(catalog, t), [catalog, t]);

  const {
    isEnterprise,

    availablePayTypes,
    currency,
    setCurrency,
    payType,
    setPayType,
    setIsEnterprise,
    bank,
    setBank,
  } = usePayPackage();

  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { accid = '', orgId } = payload || {};

  const language = useSystemLanguageValue();

  const [accountNum, setAccountNum] = useState(0);
  const [durationNum, setDurationNum] = useState<Pay.DurationNum | -1>(0);
  const [canOrder, setCanOrder] = useState(false);
  const [packageId, setPackageId] = useState<Pay.PackageId | undefined>(() => {
    const { recommendData } = packageInfo ?? {};
    return recommendData?.recommendPackageId;
  });
  const [zoneChangeVisible, setZoneChangeVisible] = useState(false);

  // 选中套餐的目录行
  const selectedCatalogPkg = useMemo(
    () => find(packageInfo?.packageData, { id: packageId }),
    [packageInfo, packageId],
  );

  // 在绘制前同步选中套餐，防止顶部活动条短暂保留上一个套餐的优惠。
  useLayoutEffect(() => {
    onSelectedPackageChange?.(zone, isEnterprise ? undefined : selectedCatalogPkg);
    return () => onSelectedPackageChange?.(zone, undefined);
  }, [zone, isEnterprise, selectedCatalogPkg, onSelectedPackageChange]);

  // 默认推荐选中不触发 PackageSelectPanel onChange，坐席数需与选中套餐保持同源（否则下单传 0）
  useEffect(() => {
    if (selectedCatalogPkg) {
      setAccountNum(selectedCatalogPkg.accountNum);
    }
  }, [selectedCatalogPkg]);

  // 币种候选（对齐 perseus）：CN 价区固定人民币（目录即使返回多币种也不可切），
  // 其余价区取选中套餐的目录币种集合
  const zoneCurrencies = useMemo(() => {
    if (narrowPriceZone(zone) === 'CN') return ['CNY'] as General.Currency[];
    return catalogPackageCurrencies(selectedCatalogPkg);
  }, [zone, selectedCatalogPkg]);

  // 选中套餐的目录 ID（新版下单传给服务端按目录内联报价）
  const regionPackageId = useMemo(() => selectedCatalogPkg?.regionPackageId, [selectedCatalogPkg]);
  const selectedPackageLabel = useMemo(
    () => catalogPackageLabel(selectedCatalogPkg, t),
    [selectedCatalogPkg, t],
  );

  // 币种缺省（对齐 perseus resolveCatalogCurrency）：持久化偏好（usePay 缓存）>
  // 价区 defaultCurrency > 目录首币种
  useEffect(() => {
    if (!zoneCurrencies.length) return;
    if (currency && zoneCurrencies.includes(currency)) return;
    const next =
      defaultCurrency && zoneCurrencies.includes(defaultCurrency as General.Currency)
        ? (defaultCurrency as General.Currency)
        : zoneCurrencies[0];
    setCurrency(next);
  }, [zone, zoneCurrencies, currency, defaultCurrency, setCurrency]);

  const [calculatePackage, setCalculatePackage] = useState<
    Pay.CalculatePackagePriceRst | undefined
  >();

  // 目录随价区刷新后重置选中；仅在价区真正切换时作废旧报价——
  // 同价区的后台目录刷新（聚焦 refetch）不重置，避免已展示的价格消失后不再重报
  const prevZoneRef = useRef(zone);
  useEffect(() => {
    if (packageInfo && narrowPriceZone(packageInfo.priceZone) === zone) {
      setPackageId(packageInfo.recommendData.recommendPackageId);
      if (prevZoneRef.current !== zone) {
        setCanOrder(false);
        setCalculatePackage(undefined);
      }
      prevZoneRef.current = zone;
    }
  }, [packageInfo, zone]);

  // 默认选中连续包月（自动续费订阅）
  const [isSubscription, setIsSubscription] = useState(true);
  const [ordering, setOrdering] = useState(false);
  const [stripePayUrl, setStripePayUrl] = useState<string>('');
  const [stripeSubscriptionVisible, setStripeSubscriptionVisible] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const { autoRenewPayTypes, selectedAutoRenewPayType, setAutoRenewPayType } =
    useAutoRenewPayType(currency);

  // 实付价格
  const actualPrice = useMemo(() => calculatePackage?.payPrice ?? 0, [calculatePackage?.payPrice]);

  const fetchStripeSign = useCallback(() => {
    const origin =
      import.meta.env.VITE_ENV === 'production'
        ? 'https://mai.zhisuitech.com'
        : 'https://feature-stq-kjds-mai.ftest.leyantech.com';
    const asaas = selectedAutoRenewPayType === 'asaas';
    return onOrder({
      idempotent: `${Date.now()}`,
      accid,
      packageType: isQuickChat ? 3 : 1,
      accountNum,
      durationNum: -1,
      discountTotalPrice: `${actualPrice}`,
      orderAction: 'ORDER',
      payType: selectedAutoRenewPayType,
      currency,
      packageId: packageId!,
      regionPackageId,
      language,
      bank,
      subscription: true,
      payResultUrl: asaas ? undefined : `${origin}/#/stripe-subscription-result?result=success`,
      cancelResultUrl: asaas ? undefined : `${origin}/#/stripe-subscription-result?result=cancel`,
    }).then((res) => {
      if (asaas) return;
      if (!res?.paymentUrl) {
        throw new Error('no paymentUrl');
      }
      setStripePayUrl(res.paymentUrl);
    });
  }, [
    accid,
    accountNum,
    actualPrice,
    bank,
    currency,
    language,
    onOrder,
    packageId,
    regionPackageId,
    selectedAutoRenewPayType,
  ]);

  useEffect(() => {
    if (!isSubscription) {
      setAgreementChecked(false);
    }
  }, [isSubscription]);

  const displayedPayTypes = isSubscription ? autoRenewPayTypes : availablePayTypes;
  const totalPrices: NonEnterpriseTotalProps['totalPrices'] = useMemo(() => {
    return {
      actualPrice: calculatePackage?.payPrice ?? 0,
      reducePrice: calculatePackage?.reducePrice ?? 0,
    };
  }, [calculatePackage]);

  if (!orgId) {
    return null;
  }

  if (catalogLoading || !packageInfo) {
    return (
      <Container>
        <ZhixiaGift />
        {catalogError ? <CatalogErrorRetry onRetry={() => refetchCatalog()} /> : <Spin />}
      </Container>
    );
  }

  let orderElement = null;
  if (isEnterprise) {
    // 企业版
    orderElement = <EnterpriseTotal />;
  } else if (canOrder) {
    orderElement = (
      <>
        <NonEnterpriseTotal currency={currency} totalPrices={totalPrices}>
          {isSubscription ? (
            <SubscriptionInfo>
              <div>
                {t('自动续费')}：{t('phrase.已开启')}
              </div>
              <div>
                {t('下次扣费')}：{calculatePackage?.afterValidEnd}
              </div>
            </SubscriptionInfo>
          ) : (
            <ServiceDate>
              {t('market.服务可使用至', { serviceDate: calculatePackage?.afterValidEnd })}
            </ServiceDate>
          )}
        </NonEnterpriseTotal>
        {isSubscription && (
          <AgreementLine>
            <Checkbox
              checked={agreementChecked}
              onChange={(e) => setAgreementChecked(e.target.checked)}
            >
              <T
                keyName="订购代表接受x"
                params={{
                  x: (
                    <TransComponent
                      content={
                        <Tooltip
                          title={
                            <div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>
                          }
                        >
                          <AgreementText>{t('自动续费协议')}</AgreementText>
                        </Tooltip>
                      }
                    />
                  ),
                }}
              />
            </Checkbox>
          </AgreementLine>
        )}
        <OrderButton
          type="primary"
          size="large"
          loading={ordering}
          onClick={() => {
            if (payType === 'airwallex' && !canPayAirwallex()) {
              Modal.info({
                title: t('market.当前客户端版本不支持 Airwallex 支付'),
                content: t('market.请重启客户端，升级至最新版本来完成订购'),
              });
              return Promise.resolve();
            }

            if (isSubscription) {
              if (!agreementChecked) {
                setConfirmModalVisible(true);
                return Promise.resolve();
              }
              setOrdering(true);
              return fetchStripeSign()
                .then(() => {
                  if (selectedAutoRenewPayType !== 'asaas') setStripeSubscriptionVisible(true);
                })
                .catch(() => {})
                .finally(() => setOrdering(false));
            }

            return onOrder({
              idempotent: `${Date.now()}`,
              accid,
              packageType: isQuickChat ? 3 : 1,
              accountNum,
              durationNum,
              discountTotalPrice: `${actualPrice}`,
              orderAction: 'ORDER',
              payType,
              currency,
              packageId: packageId!,
              regionPackageId,
              language,
              bank,
            });
          }}
        >
          {t('market.立即订购')}
        </OrderButton>
      </>
    );
  }
  return (
    <Container>
      <ZhixiaGift />
      <ZoneBar
        zone={zone}
        changeable={zones.length > 1}
        onChange={() => setZoneChangeVisible(true)}
      />
      <PackageDetail
        key={zone}
        orgId={orgId}
        packageInfo={packageInfo}
        currency={currency}
        isEnterprise={isEnterprise}
        setAccountNum={setAccountNum}
        setDurationNum={setDurationNum}
        setCanOrder={setCanOrder}
        setIsEnterprise={setIsEnterprise}
        setIsSubscription={setIsSubscription}
        onPackageChange={(isEnterprise, select) => {
          if (isEnterprise) {
            setCurrency('CNY');
          }
          setPackageId(select?.id);
        }}
        onCalculatePackageChange={setCalculatePackage}
      />
      <CurrencyCheckbox currencies={zoneCurrencies} value={currency} onChange={setCurrency} />
      {isEnterprise ? null : (
        <PayTypeCheckbox
          payTypes={displayedPayTypes}
          value={isSubscription ? selectedAutoRenewPayType : payType}
          onChange={isSubscription ? setAutoRenewPayType : setPayType}
          currency={currency}
          bank={bank}
          onBankChange={setBank}
        />
      )}
      {currency === 'CNY' && !isSubscription && (
        <div className="mt-[-12px]">
          <span className="mr-[6px]">
            {t('若需要开具公司抬头增值税发票，请使用公司支付宝进行支付')}
          </span>
          <CompanyAliHelp />
          <Help />
        </div>
      )}
      {orderElement}
      <ZoneChangeDialog
        visible={zoneChangeVisible}
        zones={zones}
        currentZone={zone}
        onCancel={() => setZoneChangeVisible(false)}
        onConfirm={(next) => {
          setZoneChangeVisible(false);
          if (next !== zone) {
            onZoneChange?.(next);
          }
        }}
      />
      {stripeSubscriptionVisible && (
        <StripeSubscriptionModal
          visible={stripeSubscriptionVisible}
          paymentUrl={stripePayUrl}
          accountNum={accountNum}
          packageId={packageId!}
          regionPackageId={regionPackageId}
          discountTotalPrice={`${actualPrice}`}
          currency={currency}
          onRefresh={fetchStripeSign}
          onSubscribed={onSubscribed}
          onCancel={() => {
            setStripeSubscriptionVisible(false);
            setStripePayUrl('');
          }}
        />
      )}
      <Modal
        visible={confirmModalVisible}
        closable={false}
        width={400}
        onOk={() => {
          setConfirmModalVisible(false);
          setAgreementChecked(true);
          setOrdering(true);
          fetchStripeSign()
            .then(() => {
              if (selectedAutoRenewPayType !== 'asaas') setStripeSubscriptionVisible(true);
            })
            .catch(() => {})
            .finally(() => setOrdering(false));
        }}
        onCancel={() => setConfirmModalVisible(false)}
        okText={t('确认订购')}
        cancelText={t('translation.取消')}
      >
        <T
          keyName="确认订购tips"
          params={{
            x: (
              <TransComponent
                content={
                  <Tooltip
                    title={<div style={{ whiteSpace: 'pre-wrap' }}>{t('自动续费协议tips')}</div>}
                  >
                    <AgreementText>{t('自动续费协议')}</AgreementText>
                  </Tooltip>
                }
              />
            ),
            y: selectedPackageLabel,
            price: `${currencySymbol[currency]} ${actualPrice}`,
          }}
        />
      </Modal>
    </Container>
  );
}

export default Package;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  > h2 {
    margin-bottom: 12px;
    font-size: 20px;
    color: var(--fett-text-primary);
  }
`;

const OrderButton = styled(StatusButton)`
  width: 260px;
  &,
  &:hover,
  &:active,
  &:focus {
    background-color: var(--fett-app-primary-color);
    border: none;
  }
`;

const ServiceDate = styled.div`
  color: var(--fett-text-tertiary);
`;

const SubscriptionInfo = styled.div`
  color: var(--fett-text-tertiary);
  > div + div {
    margin-top: 4px;
  }
`;

const AgreementLine = styled.div`
  margin-top: -12px;
`;

const AgreementText = styled.span`
  margin: 0 4px;
  color: var(--fett-app-primary-color);
  cursor: pointer;
`;
